import React, {useEffect, useMemo, useState} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  FlatList,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {Colors} from '../../../constants/Colors';
import {NavigationProps} from '../../../navigation/Routes';
import {AppRoutes} from '../../../navigation/Routes.types';
import {Images} from '../../../constants/Images';

interface ComboProduct {
  sku: string;
  image?: string;
  description: string;
  quantity: number;
  // Nulo quando o componente nao esta na tabela de preco do cliente.
  price: number | null;
  discountedPrice: number;
  operation?: string;
}
interface ComboProductsListProps {
  products: ComboProduct[];
  totalDiscountedPrice?: number;
  totalOriginalPrice?: number;
  percentageDiscounted?: number;
}

const ComboProductsList: React.FC<ComboProductsListProps> = ({
  products,
  totalDiscountedPrice,
  totalOriginalPrice,
  percentageDiscounted,
}) => {
  const navigation = useNavigation<NavigationProps>();
  const [bonificationDiscounted, setBonificationDiscounted] =
    useState<number>();

  const totalUnits = useMemo(() => {
    return products.reduce((sum, p) => sum + p.quantity, 0);
  }, [products]);

  const paidProducts = useMemo(() => {
    return products.filter(p => p.operation !== 'ZS03');
  }, [products]);

  const freeProducts = useMemo(() => {
    return products.filter(p => p.operation === 'ZS03');
  }, [products]);

  const totalPaidUnits = useMemo(() => {
    return paidProducts.reduce((sum, p) => sum + p.quantity, 0);
  }, [paidProducts]);

  const valuePaid = useMemo(() => {
    return paidProducts.reduce((sum, p) => sum + (p.price ?? 0) * p.quantity, 0);
  }, [paidProducts]);

  useEffect(() => {
    if (freeProducts?.length > 0 && valuePaid > 0) {
      const discount = valuePaid - (totalDiscountedPrice ?? 0);
      const percentage = (discount / valuePaid) * 100;
      setBonificationDiscounted(percentage > 0 ? percentage : 0);
    }
  }, [freeProducts, valuePaid, totalDiscountedPrice]);

  // ✅ Ordena os produtos: pagos primeiro, grátis no final
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      const aIsFree = a.operation === 'ZS03';
      const bIsFree = b.operation === 'ZS03';
      return Number(aIsFree) - Number(bIsFree);
    });
  }, [products]);

  const renderItem = ({item}: {item: ComboProduct}) => {
    const isFree = item.operation === 'ZS03';
    const hasPrice = item.price !== null && item.price !== undefined;
    const unitOriginalPrice = item.price ?? 0;

    // Desconto negativo ou zero nao vira indicativo: o item mantem o preco.
    const rawDiscount = bonificationDiscounted ?? percentageDiscounted ?? 0;
    const discountPercent = rawDiscount > 0 ? rawDiscount : 0;
    const hasDiscount = discountPercent > 0;

    const unitDiscountedPrice = isFree
      ? 0
      : unitOriginalPrice - (unitOriginalPrice * discountPercent) / 100;

    return (
      <View style={styles.itemContainer}>
        <View style={{position: 'relative'}}>
          <Image
            source={item.image ? {uri: item.image} : Images.emptyImgIcon}
            style={styles.image}
            resizeMode="contain"
          />
          {/* {isFree && (
            <View style={styles.freeBadge}>
              <Text style={styles.freeText}>GRÁTIS</Text>
            </View>
          )} */}
        </View>

        <TouchableOpacity style={{flex: 1}}>
          {(<Text style={styles.name}>{item.description}</Text>)}
          {isFree && (
            <View style={styles.freeBadge}>
              <Text style={styles.freeText}>GRÁTIS</Text>
            </View>
          )}
          <Text style={styles.sub}>Qtd: {item.quantity}</Text>
          {!isFree && hasPrice && (
            <>
              {hasDiscount && (
                <Text style={styles.sub}>
                  Preço da caixa: R${' '}
                  {(Number(unitOriginalPrice) || 0).toFixed(2).replace('.', ',')}
                </Text>
              )}
              <Text style={hasDiscount ? styles.discount : styles.sub}>
                Nesse combo: R${' '}
                {(Number(unitDiscountedPrice) || 0).toFixed(2).replace('.', ',')}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={{paddingVertical: 25}}>
      <Text style={styles.title}>Produtos do Combo</Text>
      <FlatList
        data={sortedProducts}
        keyExtractor={(item, index) => `${item.sku}-${index}`}
        renderItem={renderItem}
        scrollEnabled={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  title: {
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 10,
  },
  itemContainer: {
    flexDirection: 'row',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GrayPallete.lightgrayTwo,
    alignItems: 'center',
  },
  image: {
    width: 60,
    height: 60,
    marginRight: 10,
    borderRadius: 4,
  },
  name: {
    fontWeight: 'bold',
    fontSize: 14,
    marginBottom: 2,
    color: Colors.GrayPallete.grayOne,
  },
  sub: {
    fontSize: 12,
    color: Colors.GrayPallete.lightgrayThree,
  },
  discount: {
    fontSize: 12,
    color: Colors.RedPallete.red,
    marginTop: 2,
  },
  freeBadge: {
    backgroundColor: Colors.GreenPallete.greenOne,
    paddingVertical: 2,
    borderRadius: 4,
    margin: 5,
    width: '20%',
    alignItems: 'center',
  },
  freeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
});

export default ComboProductsList;
